import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "components/header";
import { SidePanel } from "components/side-panel";
import { Container } from "components/container";
import styles from "./assets/scss/main.module.scss";
import { useSelector, useDispatch } from "react-redux";
import { uiActions } from "store/ui/ui-slice";
import { getRequest } from "utils/request";

interface Notification {
  id: string;
  message: string;
  created_at: string;
  is_read: boolean;
  read_at: string | null;
  member: string;
}

interface NotificationsResponse {
  notifications: Notification[];
  unread_count: number;
  total_count: number;
}

interface IHeaderProps {
  icon: any;
  title: string;
  timerRunning?: boolean;
  elapsedTime?: number;
  onOpenTimer?: () => void;
  isPaused?: boolean;
}

interface IProps {
  buttonLabel?: string;
  cardLabel?: string;
  paragraphContent?: string;
  welcomeText?: string;
  onButtonClick?: () => void;
  cardLabelStyle?: React.CSSProperties;
  children: React.ReactNode;
  header: IHeaderProps;
  action?: {
    title: string;
    clickHandler: () => void;
    disabled?: boolean; // Add disabled property
  };
}

let notificationInterval: NodeJS.Timeout | null = null;

const Main = ({ children, header, action, welcomeText }: IProps) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission>("default");
  const [lastNotificationTime, setLastNotificationTime] = useState<string>(
    localStorage.getItem("lastNotificationTime") || new Date(0).toISOString(),
  );
  const app = useSelector((state: any) => state.app?.allData || {});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Request notification permission
  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      console.log("This browser does not support notifications");
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);

      if (permission === "granted") {
        console.log("Notification permission granted");
      } else {
        console.log("Notification permission denied");
      }
    } catch (error) {
      console.error("Error requesting notification permission:", error);
    }
  };

  // Display browser notification
  const displayBrowserNotification = (notification: Notification) => {
    if (notificationPermission !== "granted") return;

    const notificationOptions: NotificationOptions = {
      body: notification.message,
      icon: "/favicon.ico",
      badge: "/favicon.ico",
      tag: notification.id,
      requireInteraction: false,
      silent: false,
    };

    const browserNotification = new Notification(
      "New Notification",
      notificationOptions,
    );

    // Handle notification click
    browserNotification.onclick = () => {
      window.focus();
      browserNotification.close();
    };

    // Auto close after 5 seconds
    setTimeout(() => {
      browserNotification.close();
    }, 5000);
  };

  // Compare UTC timestamps to find new notifications
  const findNewNotifications = (
    currentNotifications: Notification[],
    lastCheckedTime: string,
  ): Notification[] => {
    const lastChecked = new Date(lastCheckedTime).getTime();

    return currentNotifications.filter((notification) => {
      const notificationTime = new Date(notification.created_at).getTime();
      return notificationTime > lastChecked;
    });
  };

  const fetchNotifications = async () => {
    if (!app.memberId) return;

    try {
      const response = await getRequest(
        `notification-list/${app.memberId}`,
        {},
        navigate,
      );

      if (response?.data.isOk) {
        const notificationsData: NotificationsResponse = response.data.data;
        const currentNotifications = notificationsData.notifications;

        // Find notifications that are newer than our last check
        const newNotifications = findNewNotifications(
          currentNotifications,
          lastNotificationTime,
        );

        // If there are new notifications, show browser notifications
        if (newNotifications.length > 0) {
          // Sort by creation time (newest first) and show the latest one
          const sortedNewNotifications = [...newNotifications].sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          );

          // Show browser notification for the latest new notification
          const latestNotification = sortedNewNotifications[0];
          displayBrowserNotification(latestNotification);

          // Update Redux store for in-app notifications
          dispatch(
            uiActions.updateNotification({
              message: latestNotification.message,
              level: "success",
            }),
          );

          // Update the last notification time to the most recent one
          const latestNotificationTime = sortedNewNotifications[0].created_at;
          setLastNotificationTime(latestNotificationTime);
          localStorage.setItem("lastNotificationTime", latestNotificationTime);
        }

        // Update local state with all notifications
        setNotifications(currentNotifications);

        // Update notification count in localStorage for other components if needed
        localStorage.setItem(
          "notificationCount",
          currentNotifications.length.toString(),
        );
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    // Check initial notification permission
    if ("Notification" in window) {
      setNotificationPermission(Notification.permission);

      // If permission wasn't requested yet, ask for it
      if (Notification.permission === "default") {
        requestNotificationPermission();
      }
    }

    // Fetch notifications immediately when component mounts
    fetchNotifications();

    // Set up notification interval
    if (!notificationInterval) {
      notificationInterval = setInterval(fetchNotifications, 3 * 60 * 1000); // 3 minutes
    }

    // Clean up interval on unmount
    return () => {
      if (notificationInterval) {
        clearInterval(notificationInterval);
        notificationInterval = null;
      }
    };
  }, [app.memberId, lastNotificationTime]);

  return (
    <>
      <Header />
      <div className={styles.bodyContainer}>
        <SidePanel />
        <Container
          headerIcon={header.icon}
          headerLabel={header?.title}
          buttonLabel={action?.title}
          onButtonClick={action?.clickHandler}
          buttonDisabled={action?.disabled} // Pass the disabled prop to Container
          welcomeText={welcomeText}
        >
          {children}
        </Container>
      </div>
    </>
  );
};

export default Main;
